import { AbstractControl, UntypedFormArray, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { FileModel, StringResource } from '@ca/core/models';
import { getWithoutHtmlTags } from 'src/utils/text/text-utils';
import * as FileUtils from '../file/file-utils';
import { URL_PATTERN, WORKFLOW_TITLE_PATTERN } from './../../app/shared/constants/constants';
import { DESCRIPTION_LENGTH_LIMIT, SUMMARY_LENGTH_LIMIT, TITLE_LENGTH_LIMIT } from './../../app/shared/constants/form-validators';

export class CustomValidators {
    static CheckedCountValidator(min: number = 0, max: number = -1, isChecked: boolean = true): ValidatorFn {
        return (array: UntypedFormArray): ValidationErrors => {
            const checkedCount = (<Array<boolean>>array.value).filter(checked => checked === isChecked).length;
            const errorObj: ValidationErrors = {};

            if (max < 0) {
                max = array.length;
            }
            if (checkedCount < min) {
                errorObj['minLength'] = `Number of ${isChecked ? '' : 'un'}checked items is less than [${min}].`;
            } else if (checkedCount > max) {
                errorObj['maxLength'] = `Number of ${isChecked ? '' : 'un'}checked items is more than [${max}].`;
            }

            return errorObj;
        };
    }

    static ModelTitle(maxLength: number = TITLE_LENGTH_LIMIT): ValidatorFn | null {
        return Validators.compose([Validators.required, Validators.maxLength(maxLength), Validators.pattern(WORKFLOW_TITLE_PATTERN)]);
    }

    static SummaryValidation(): ValidatorFn | null {
        return Validators.compose([Validators.required, Validators.maxLength(SUMMARY_LENGTH_LIMIT)]);
    }

    static ModelDescription(maxLength: number = DESCRIPTION_LENGTH_LIMIT): ValidatorFn | null {
        return Validators.compose([Validators.required, Validators.maxLength(maxLength)]);
    }

    static CommonName(): ValidatorFn {
        return Validators.pattern(/^[\\.\w\s\u00e4\u00f6\u00fc\u00c4\u00d6\u00dc\u00df-]+$/);
    }

    static PhoneNumber(): ValidatorFn {
        return Validators.pattern(/^[0-9+-]+$/);
    }

    static HttpOrHttpsUrl(): ValidatorFn {
        return Validators.pattern(URL_PATTERN);
    }

    static NameDoesNotExistInFiles(files: FileModel[]): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            return !FileUtils.isFileNameWithExtensionContainedInFiles(control.value, files) ? null : { nameExistsInArray: { value: control.value } };
        };
    }

    static decodeURIComponentAble(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            try {
                decodeURIComponent(control.value);
            } catch (error) {
                return { invalidURI: { value: control.value } };
            }
            return null;
        };
    }

    static NotContainingOnlyWhitespaces(): ValidatorFn {
        return Validators.pattern(/[^ ]/);
    }

    /**
     * validate if default value is part of provided options/items
     */
    static isDefaultValidValidator(items: StringResource[]): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const foundValuesInUiSpec = items.filter((item: StringResource) => item.key === control.value);
            const isDefaultValueIncludedInItems = foundValuesInUiSpec.length === 1;
            return isDefaultValueIncludedInItems ? null : { invalidDefaultValue: { value: control.value } };
        };
    }

    static SupportedFileType(supportedFileTypes?: string[]): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (supportedFileTypes != null && supportedFileTypes.length > 0 && control.value != null && control.value !== '') {
                const fileName = FileUtils.getFileNameWithExtension(control.value);
                return FileUtils.isSupportedType(fileName, supportedFileTypes) ? null : { notSupportedFileType: { value: control.value } };
            }
            return null;
        };
    }

    static requiredWithoutHtmlTags(): ValidatorFn {
        return (control: AbstractControl): { [errorName: string]: boolean } | null => {
            const isEmpty = control.value ? getWithoutHtmlTags(control.value)?.length === 0 : true;

            return isEmpty ? { required: true } : null;
        };
    }

    static maxLengthWithoutHtmlTags(maxLength: number): ValidatorFn {
        return (control: AbstractControl): { [errorName: string]: boolean } | null => {
            if (control.value) {
                return getWithoutHtmlTags(control.value)!.length > maxLength ? { maxLength: true } : null;
            }
            return null;
        };
    }
}
